import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import ProjectsListPage from "./pages/ProjectsListPage";
import CreateProjectPage from "./pages/CreateProjectPage";
import SketchPage from "./pages/SketchPage";
import ProjectDetailPage from "./components/ProjectDetailPage";
import EventsPage from "./components/EventsPage";
import LDTManagerModal from "./components/LDTManagerModal";
import "./App.css";

// ── App ────────────────────────────────────────────────────────────────
// Root component for the Signify Sports Tool 2.0 restructure. React
// Router v7 drives navigation between four top-level pages:
//
//   /                       → redirect to /projects
//   /projects               → ProjectsListPage (active + archive tables)
//   /projects/new           → CreateProjectPage (metadata + address)
//   /projects/:id/edit      → CreateProjectPage in edit mode
//   /projects/:id/sketch    → SketchPage (existing wizard + map)
//   /projects/:id           → ProjectDetailPage (calculations table)
//   /events                 → existing dashboard (kept as-is)
//
// The shared AppHeader stays mounted across routes; it owns the
// "+ Neues Projekt" button + Events / LDT-Bibliothek shortcuts.
// ───────────────────────────────────────────────────────────────────────

// Signify mark — official SVG (a green-to-teal gradient wordmark)
// lives at /signify-logo.svg in the public folder. The SVG is vector,
// crisp at any size, and carries the brand gradient natively so we
// don't have to fake it in CSS. The legacy .png is kept as a fallback
// if the SVG ever 404s during local dev.
const SignifyLogo = () => {
  const [errored, setErrored] = React.useState(false);
  if (errored) {
    // Wordmark fallback in the official primary green.
    return (
      <span style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700, fontSize: 22,
        color: "var(--brand-primary-deep)",
        letterSpacing: "-0.02em",
      }}>signify</span>
    );
  }
  return (
    <img
      src="/signify-logo.svg"
      alt="Signify"
      onError={() => setErrored(true)}
    />
  );
};

// ── AppHeader ─────────────────────────────────────────────────────────
// Top-of-page chrome shared across every route. All styling lives in
// .app-header* utility classes in App.css so a future theme change only
// touches CSS, not this component.
// ──────────────────────────────────────────────────────────────────────
const AppHeader = ({ onOpenLdtModal, onOpenEvents }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path;
  return (
    <header className="app-header">
      <div className="app-header-left">
        <Link to="/projects" className="app-header-brand">
          <SignifyLogo />
          <span className="app-header-divider" aria-hidden="true" />
          <span className="app-header-subtitle">Sports Tool 2.0</span>
        </Link>
        <nav className="app-header-nav" aria-label="Hauptnavigation">
          <Link
            to="/projects"
            className={`app-header-nav-link ${isActive("/projects") ? "is-active" : ""}`}
          >
            Alle Projekte
          </Link>
          <button type="button" className="app-header-nav-btn" onClick={onOpenLdtModal}>
            LDT-Bibliothek
          </button>
          <button type="button" className="app-header-nav-btn" onClick={onOpenEvents}>
            Events
          </button>
        </nav>
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => navigate("/projects/new")}
      >
        + Neues Projekt
      </button>
    </header>
  );
};

const App = () => {
  // LDT modal + Events overlay are stateful at the root because both
  // are reachable from the header on any page. The router doesn't
  // need to know about them — they're modal overlays, not routes.
  const [isLdtModalOpen, setIsLdtModalOpen] = useState(false);
  const [showEvents, setShowEvents] = useState(false);

  return (
    <BrowserRouter>
      <div className="app">
        <AppHeader
          onOpenLdtModal={() => setIsLdtModalOpen(true)}
          onOpenEvents={() => setShowEvents(true)}
        />

        {/* Modal overlays anchored to the root */}
        <LDTManagerModal isOpen={isLdtModalOpen} onClose={() => setIsLdtModalOpen(false)} />
        {showEvents && <EventsPage onBack={() => setShowEvents(false)} />}

        {/* Routes wrapper: flex:1 + min-height:0 establishes a scroll
            context so list/edit pages can scroll their content while the
            sketch page (which uses inner flex layout) stays full-height. */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectsListPage />} />
            <Route path="/projects/new" element={<CreateProjectPage />} />
            <Route path="/projects/:id/edit" element={<CreateProjectPage />} />
            <Route path="/projects/:id/sketch" element={<SketchPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </div>
      </div>
    </BrowserRouter>
  );
};

export default App;
